import { validate } from 'uuid';
import type {
    CredentialClaimValue,
    CredentialFilterData,
    FileFilterData,
    LinkedRef,
    ResourceCredential,
    ResourceDraft,
    ResourceFile,
} from '../api';
import { UnknownDraftCredential } from './credential/draft/unknown';
import { DraftCredentialWithClaims } from './credential/draft/with-claims';
import { UnknownCredential } from './credential/unknown';
import { UnknownVerifiableCredential } from './credential/verifiable/unknown';
import { VerifiableCredentialWithClaims } from './credential/verifiable/with-claims';
import { CredentialWithClaims } from './credential/with-claims';
import {
    ApiClient,
    Claims,
    Ctor,
    FileBlob,
    FileDescriptor,
    LinkedCredentialDescriptor,
    LinkedFileDescriptor,
} from './types';

type NormalizedLinkedFileId = {
    linkedId: string;
    resourceId?: string;
};

type NormalizedLinkedCredentialId = {
    linkedId: string;
    resourceId?: string;
    isDraft: boolean;
};

export class LinkedCredential<T extends Claims | unknown> implements LinkedCredentialDescriptor {
    static normalizeLinkedCredentialId(id: string): NormalizedLinkedCredentialId {
        const expectedCredentialResourcePrefix = 'urn:credential:';
        const expectedDraftResourcePrefix = 'urn:draft:';

        if (id.startsWith('urn:')) {
            if (id.startsWith(expectedDraftResourcePrefix)) {
                return {
                    isDraft: true,
                    linkedId: id,
                    resourceId: id.slice(expectedDraftResourcePrefix.length),
                };
            } else if (id.startsWith(expectedCredentialResourcePrefix)) {
                return {
                    isDraft: false,
                    linkedId: id,
                    resourceId: id.slice(expectedCredentialResourcePrefix.length),
                };
            } else {
                throw new Error(`The ID does not refer to a credential nor to a draft: ${id}`);
            }
        }

        // We assume it's a link to a verifiable credential instead of a draft in case of a uuid
        // without the expected draft prefix
        if (validate(id)) {
            return {
                isDraft: false,
                linkedId: expectedCredentialResourcePrefix + id,
                resourceId: id,
            };
        }

        return {
            isDraft: false,
            linkedId: id,
        };
    }

    public id: string;
    public type: LinkedCredentialDescriptor['type'] = 'LinkedCredential';

    constructor(client: ApiClient, ref: LinkedRef);
    constructor(client: ApiClient, id: string, digestMultibase?: string);
    constructor(
        private client: ApiClient,
        idOrRef: string | LinkedRef,
        public digestMultibase?: string,
    ) {
        const id = typeof idOrRef === 'string' ? idOrRef : idOrRef.blobId;
        this.id = LinkedCredential.normalizeLinkedCredentialId(id).linkedId;
    }

    static createCredentialClaimValue(id: string, digestMultibase?: string): CredentialClaimValue {
        return {
            kind: 'LINK',
            value: {
                id,
                digestMultibase,
            },
        };
    }

    toJSON(): CredentialClaimValue {
        return LinkedCredential.createCredentialClaimValue(this.id, this.digestMultibase);
    }

    private async getLinkedCredentialResource(): Promise<ResourceCredential> {
        const idDescriptor = LinkedCredential.normalizeLinkedCredentialId(this.id);

        if (idDescriptor.resourceId) {
            return this.client.credentials.credentialLatest(idDescriptor.resourceId);
        }

        const filterData: CredentialFilterData = {
            id: {
                operator: 'EQUAL',
                value: idDescriptor.linkedId,
            },
        };

        if (this.digestMultibase) {
            filterData.digestMultibase = {
                operator: 'EQUAL',
                value: this.digestMultibase,
            };
        }

        const result = await this.client.credentials.credentialSearch({
            filter: [
                {
                    data: filterData,
                },
            ],
        });

        const linkedCredential = result.items.at(0);

        if (!linkedCredential) {
            throw new Error(
                `Could not resolve credential resource. Please ensure that the linked credential exists or that you have access to it`,
            );
        }

        return linkedCredential;
    }

    private async getLinkedDraftResource(): Promise<ResourceDraft> {
        const idDescriptor = LinkedCredential.normalizeLinkedCredentialId(this.id);

        if (idDescriptor.resourceId) {
            return this.client.drafts.draftLatest(idDescriptor.resourceId);
        }

        throw new Error(
            `Could not resolve draft credential resource. Please ensure that the linked draft exists or that you have access to it`,
        );
    }

    async dereference(): Promise<UnknownCredential<T>> {
        const idDescriptor = LinkedCredential.normalizeLinkedCredentialId(this.id);
        if (idDescriptor.isDraft) {
            return new UnknownCredential(await this.dereferenceDraft());
        } else {
            return new UnknownCredential(await this.dereferenceVerifiableCredential());
        }
    }

    async dereferenceDraft(): Promise<UnknownDraftCredential<T>> {
        const linkedDraftResource = await this.getLinkedDraftResource();

        return new UnknownDraftCredential(this.client, linkedDraftResource.data, linkedDraftResource);
    }

    async dereferenceVerifiableCredential(): Promise<UnknownVerifiableCredential<T>> {
        const linkedCredentialResource = await this.getLinkedCredentialResource();

        return new UnknownVerifiableCredential(this.client, linkedCredentialResource);
    }

    async dereferenceAs<K extends Claims>(
        claimsModel: Ctor<T extends Claims ? (K extends T ? K : never) : K>,
    ): Promise<CredentialWithClaims<K>> {
        return (await this.dereference()).map(claimsModel);
    }

    async dereferenceDraftAs<K extends Claims>(
        claimsModel: Ctor<T extends Claims ? (K extends T ? K : never) : K>,
    ): Promise<DraftCredentialWithClaims<K>> {
        return (await this.dereferenceDraft()).map(claimsModel);
    }

    async dereferenceVerifiableCredentialAs<K extends Claims>(
        claimsModel: Ctor<T extends Claims ? (K extends T ? K : never) : K>,
    ): Promise<VerifiableCredentialWithClaims<K>> {
        return (await this.dereferenceVerifiableCredential()).map(claimsModel);
    }
}

type DownloadableFile = FileDescriptor & {
    getReader: () => Promise<ReadableStream>;
    download: () => Promise<FileBlob>;
};

export class LinkedFile implements LinkedFileDescriptor {
    static normalizeLinkedFileId(id: string): NormalizedLinkedFileId {
        const expectedFileResourcePrefix = 'urn:file:';

        if (id.startsWith('urn:')) {
            if (!id.startsWith(expectedFileResourcePrefix)) {
                throw new Error(`The ID does not refer to a file: ${id}`);
            }

            return {
                linkedId: id,
                resourceId: id.slice(expectedFileResourcePrefix.length),
            };
        }

        if (validate(id)) {
            return {
                linkedId: expectedFileResourcePrefix + id,
                resourceId: id,
            };
        }

        return {
            linkedId: id,
        };
    }

    public id: string;
    public type: LinkedFileDescriptor['type'] = 'LinkedFile';

    constructor(client: ApiClient, ref: LinkedRef);
    constructor(client: ApiClient, id: string, digestMultibase?: string);
    constructor(
        private client: ApiClient,
        idOrRef: string | LinkedRef,
        public digestMultibase?: string,
    ) {
        const id = typeof idOrRef === 'string' ? idOrRef : idOrRef.blobId;
        this.id = LinkedFile.normalizeLinkedFileId(id).linkedId;
    }

    static createCredentialClaimValue(id: string, digestMultibase?: string): CredentialClaimValue {
        return {
            kind: 'FILE',
            value: {
                id,
                digestMultibase,
            },
        };
    }

    toJSON(): CredentialClaimValue {
        return LinkedFile.createCredentialClaimValue(this.id, this.digestMultibase);
    }

    private async getLinkedFileResource(): Promise<ResourceFile> {
        const idDescriptor = LinkedFile.normalizeLinkedFileId(this.id);

        if (idDescriptor.resourceId) {
            return this.client.files.fileLatest(idDescriptor.resourceId);
        }

        const filterData: FileFilterData = {
            id: {
                operator: 'EQUAL',
                value: idDescriptor.linkedId,
            },
        };

        if (this.digestMultibase) {
            filterData.digestMultibase = {
                operator: 'EQUAL',
                value: this.digestMultibase,
            };
        }

        const result = await this.client.files.fileSearch({
            filter: [
                {
                    data: filterData,
                },
            ],
        });

        const linkedFile = result.items.at(0);

        if (!linkedFile) {
            throw new Error(
                `Could not resolve file resource. Please ensure that the linked file exists or that you have access to it`,
            );
        }

        return linkedFile;
    }

    async dereference(): Promise<DownloadableFile> {
        const file = await this.getLinkedFileResource();

        return {
            id: file.id,
            revision: file.revision,
            etag: file.etag,
            data: file.data,

            getReader: async () => {
                const response = await this.client.files.fileDownload(file.id, file.revision);
                return response.stream();
            },
            download: async () => {
                const response = await this.client.files.fileDownload(file.id, file.revision);
                return response.blob();
            },
        };
    }
}

export class Rfc3339Date {
    private readonly date: Date;

    constructor(year: number, month: number, day: number);
    constructor(dateString: string);
    constructor(date: Date);
    constructor();
    constructor(...args: any[]) {
        if (args.length === 1 && (typeof args[0] === 'string' || args[0] instanceof Date)) {
            const parsedDate = new Date(args[0]);

            this.date = new Date(Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getUTCDate()));
        } else if (
            args.length === 3 &&
            typeof args[0] === 'number' &&
            typeof args[1] === 'number' &&
            typeof args[2] === 'number'
        ) {
            this.date = new Date(Date.UTC(args[0], args[1], args[2]));
        } else {
            this.date = new Date();
        }

        this.date.setUTCHours(0, 0, 0, 0);
    }

    toString(): string {
        return this.date.toISOString().split('T')[0];
    }

    toJSON(): string {
        return this.toString();
    }

    toDate(): Date {
        return new Date(this.date);
    }
}

export class Rfc3339DateTime {
    private readonly date: Date;

    constructor(year: number, month: number, day: number);
    constructor(dateString: string);
    constructor(date: Date);
    constructor();
    constructor(...args: any[]) {
        if (args.length === 1 && (typeof args[0] === 'string' || args[0] instanceof Date)) {
            this.date = new Date(args[0]);
        } else if (
            args.length === 3 &&
            typeof args[0] === 'number' &&
            typeof args[1] === 'number' &&
            typeof args[2] === 'number'
        ) {
            this.date = new Date(args[0], args[1], args[2]);
        } else {
            this.date = new Date();
        }
    }

    toString(): string {
        return this.date.toISOString();
    }

    toJSON(): string {
        return this.toString();
    }

    toDate(): Date {
        return new Date(this.date);
    }
}
